import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    TextField
} from '@mui/material';
import { FaEnvelope } from 'react-icons/fa';
import { Save, Close } from '@mui/icons-material';
import Popup from 'reactjs-popup';

import './AddEmailTemplate.css';
import EmailTagInput from '../../Commen_Component/EmailTagInput';
import { EmailTemplateService } from '../../Commen_Services/EmailTemplate.service';
import customToast from '../../Commen_Component/Toast/CustomToast';
import { useLoading } from '../../Loader/LoadingContext';
import { PageEnums } from '../../../_enums/enum.services';

interface EmailTemplateFormData {
    emailTemplateId: number;
    toEmailAddress: string[];
    ccEmailAddress: string[];
    subject: string;
    body: string;
    isActive: boolean;
}

const parseEmailString = (str: string | null | undefined): string[] => {
    if (!str) return [];
    return str.split(',').map((e) => e.trim()).filter(Boolean);
};

const emailArrayToString = (arr: string[]): string => {
    return arr.map((e) => e.trim()).filter(Boolean).join(',');
};

const emptyFormData: EmailTemplateFormData = {
    emailTemplateId: 0,
    toEmailAddress: [],
    ccEmailAddress: [],
    subject: '',
    body: '',
    isActive: true,
};

const AddEmailTemplate = () => {
    const navigate = useNavigate();
    const { pageId } = useParams<{ pageId: string }>();
    const { action } = useParams<{ action: string }>();
    const { setLoading } = useLoading();

    const [formData, setFormData] = useState<EmailTemplateFormData>(emptyFormData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewData, setIsViewData] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        const isViewRoute = action === 'view';
        const isEditRoute = Number(pageId) > 0 && action !== 'view';
        setIsViewData(isViewRoute);
        setIsEditMode(isEditRoute || (Number(pageId) > 0 && !isViewRoute));

        if (pageId && Number(pageId) > 0) {
            loadData(Number(pageId));
        } else if (!pageId) {
            setFormData(emptyFormData);
            setErrors({});
            setIsEditMode(false);
            setIsViewData(false);
        }
    }, [pageId, action]);

    const loadData = async (id: number) => {
        try {
            setLoading(true);
            const response = await EmailTemplateService.getEmailTemplateDetail(id);

            if (response?.status === true && response?.data) {
                setFormData({
                    emailTemplateId: response.data.emailTemplateId || 0,
                    toEmailAddress: parseEmailString(response.data.toEmailAddress),
                    ccEmailAddress: parseEmailString(response.data.ccEmailAddress),
                    subject: response.data.subject || '',
                    body: response.data.body || '',
                    isActive: response.data.isActive ?? true,
                });
                setIsEditMode(true);
            } else {
                customToast.Warning(response?.message || 'Data not found.');
                setFormData(emptyFormData);
            }
        } catch (error) {
            customToast.error('Unable to load email template details.');
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        const nextErrors: Record<string, string> = {};

        if (!formData.subject || formData.subject.trim() === '') {
            nextErrors.subject = 'Please enter a valid Subject';
        }

        if (!formData.toEmailAddress || formData.toEmailAddress.length === 0) {
            nextErrors.toEmailAddress = 'Please enter at least one valid To Email Address';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement;
        const name = target.name;
        const value = target.value;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleChangeForIsActive = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setFormData((prev) => ({
            ...prev,
            isActive: checked,
        }));
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!validate()) {
            customToast.error('Please fix validation errors.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                emailTemplateId: formData.emailTemplateId ?? 0,
                toEmailAddress: emailArrayToString(formData.toEmailAddress),
                ccEmailAddress: emailArrayToString(formData.ccEmailAddress),
                subject: formData.subject,
                body: formData.body,
                isActive: formData.isActive,
            };

            const response = await EmailTemplateService.saveOrUpdateEmailTemplate(payload);

            if (response?.status === true) {
                customToast.success(response.message || 'Data saved successfully.');
                navigate(`/Report/view/${PageEnums.EmailTemplate}`);
                return;
            }

            if (response?.status === false && response?.message) {
                customToast.error(response.message);
                return;
            }

            if (response?.errors) {
                setErrors(response.errors);
                customToast.error('Validation failed.');
                return;
            }

            customToast.error('Unable to save email template.');
        } catch (error) {
            customToast.error('API failed while saving email template.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setShowConfirmation(false);
    };

    const handleConfirmExit = () => {
        navigate(`/Report/view/${PageEnums.EmailTemplate}`);
    };

    return (
        <div>
            {/* Header section matching AddEmployee header */}
            <div className="shadow">
                <div className="bg-line">
                    <span className="bg-head">
                        <FaEnvelope /> &nbsp;{Number(pageId) > 0 && action !== 'view' ? <>Update </> : isViewData ? <>View </> : <>Add </>} Email Template
                    </span>
                </div>
                <hr className="colorgraph" />
            </div>

            {/* Single Cohesive Form Container */}
            <div style={{ padding: '20px 15px', maxWidth: '850px', margin: '0' }}>
                {/* 1. Subject Field */}
                <div style={{ marginBottom: '18px' }}>
                    <TextField
                        id="standard-basic"
                        variant="standard"
                        name="subject"
                        value={formData.subject}
                        label="Subject"
                        onChange={handleChange}
                        focused={!!formData.subject}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        fullWidth
                        InputProps={{
                            readOnly: isViewData,
                            style: {
                                color: 'var(--body-main-dark-color)',
                                fontSize: '14px',
                                borderColor: errors.subject ? 'red' : undefined
                            }
                        }}
                        InputLabelProps={{
                            style: {
                                color: 'var(--body-main-dark-color)',
                                fontSize: '14px',
                                fontWeight: 600,
                                borderColor: errors.subject ? 'red' : undefined
                            },
                        }}
                        FormHelperTextProps={{
                            style: {
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                        }}
                    />
                </div>

                {/* 2. To Email Address (Chips) */}
                <EmailTagInput
                    label="To:"
                    emails={formData.toEmailAddress}
                    onChange={(newEmails) => {
                        setFormData((prev) => ({ ...prev, toEmailAddress: newEmails }));
                        if (errors.toEmailAddress) setErrors((prev) => ({ ...prev, toEmailAddress: '' }));
                    }}
                    error={errors.toEmailAddress}
                    disabled={isViewData}
                    placeholder="Enter recipient email address"
                    required
                />

                {/* 3. CC Email Address (Chips) (optional) */}
                <EmailTagInput
                    label="Cc:"
                    emails={formData.ccEmailAddress}
                    onChange={(newEmails) => {
                        setFormData((prev) => ({ ...prev, ccEmailAddress: newEmails }));
                        if (errors.ccEmailAddress) setErrors((prev) => ({ ...prev, ccEmailAddress: '' }));
                    }}
                    error={errors.ccEmailAddress}
                    disabled={isViewData}
                    placeholder="Enter optional CC email address"
                />

                {/* 4. Email Body Section */}
                <div style={{ marginBottom: '20px' }}>
                    <TextField
                        id="standard-multiline-static"
                        variant="standard"
                        name="body"
                        value={formData.body}
                        label="Email Body / Content Template"
                        onChange={handleChange}
                        multiline
                        minRows={6}
                        focused={!!formData.body}
                        error={!!errors.body}
                        helperText={errors.body}
                        fullWidth
                        InputProps={{
                            readOnly: isViewData,
                            style: {
                                color: 'var(--body-main-dark-color)',
                                fontSize: '14px',
                            }
                        }}
                        InputLabelProps={{
                            style: {
                                color: 'var(--body-main-dark-color)',
                                fontSize: '14px',
                                fontWeight: 600,
                            },
                        }}
                        FormHelperTextProps={{
                            style: {
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                        }}
                    />
                </div>

                {/* 6. Is Active Slider Switch (Right-aligned at the end of form) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <div className="Emp_isActive">
                        <label className="Emp_Switch">
                            <input
                                type="checkbox"
                                name="isActive"
                                onChange={handleChangeForIsActive}
                                checked={formData.isActive}
                                disabled={isViewData}
                            />
                            <span className="Emp_slider"></span>
                        </label>
                        <span style={{ fontSize: '14px', color: 'var(--body-main-dark-color)', fontWeight: 600, marginLeft: '8px' }}>
                            Is Active
                        </span>
                    </div>
                </div>

                {/* 7. Bottom Action Buttons */}
                <div className="Area_btn" style={{ paddingLeft: 0 }}>
                    {!isViewData && (
                        <button
                            className="btn123"
                            onClick={handleSubmit}
                            style={{ paddingRight: 12, height: '30px' }}
                        >
                            <Save />&nbsp;{Number(pageId) > 0 ? <>Update</> : <>Save</>}
                        </button>
                    )}
                    &nbsp;
                    <button
                        className="btn123"
                        onClick={() => setShowConfirmation(true)}
                        style={{ paddingRight: 12, height: '30px' }}
                    >
                        <Close />&nbsp;Close
                    </button>

                    {showConfirmation && (
                        <Popup
                            contentStyle={{ height: 105, width: 275, padding: 0 }}
                            position="top center"
                            open={showConfirmation}
                            onClose={() => setShowConfirmation(false)}
                        >
                            <div className="confirmation-modal">
                                <p>Are you sure you want to exit? <br />(Any unsaved changes will be lost.)</p>
                                <div className="canel_btns">
                                    <button className="btn123" style={{ marginRight: 10 }} onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button className="btn123" style={{ marginLeft: 10 }} onClick={handleConfirmExit}>
                                        Yes, Exit
                                    </button>
                                </div>
                            </div>
                        </Popup>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddEmailTemplate;
